import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface MedicalRecord {
  id: string
  patientRecordId: string
  researchId: string
  patientName: string
  diseaseName: string
  age?: number
  gender?: string
  transplantCount?: number
  recordDate: string
  hospitalId: string
  hospitalName: string
  doctorId: string
  doctorName: string
  recordType: 'doctor' | 'patient'
  subjectiveNote: string
  objectiveNote: string
  assessmentNote: string
  planNote: string
  additionalNote: string
  createdAt: string
  updatedAt: string
}

interface MedicalRecordState {
  records: MedicalRecord[]
  addRecord: (record: Omit<MedicalRecord, 'id' | 'createdAt' | 'updatedAt'>) => string
  updateRecord: (id: string, data: Partial<MedicalRecord>) => void
  deleteRecord: (id: string) => void
  getRecord: (id: string) => MedicalRecord | undefined
  getRecordsByPatient: (patientId: string) => MedicalRecord[]
  getRecordsByHospital: (hospitalId: string) => MedicalRecord[]
}

// 初期サンプルデータ
const initialRecords: MedicalRecord[] = [
  {
    id: 'med-001',
    patientRecordId: 'pat-001',
    researchId: 'R01166',
    patientName: 'KuKn',
    diseaseName: '潰瘍性大腸炎',
    age: 33,
    gender: '女性',
    transplantCount: 3,
    recordDate: '2025-11-28',
    hospitalId: 'hosp-001',
    hospitalName: 'Baseクリニック赤坂',
    doctorId: 'doc-001',
    doctorName: '吉岡 紘平',
    recordType: 'doctor',
    subjectiveNote: '睡眠が改善したように思う 何となく調子は良い',
    objectiveNote: '腹部平坦、軟、圧痛なし\n腸蠕動音正常',
    assessmentNote: '移植後の経過は良好。症状の軽度改善を認める。',
    planNote: '経過観察を継続\n次回診察は1ヶ月後を予定\n食事指導：発酵食品の摂取を推奨',
    additionalNote: '本日で3回コース終了',
    createdAt: '2025-11-28T10:00:00Z',
    updatedAt: '2025-11-28T10:00:00Z',
  },
  {
    id: 'med-002',
    patientRecordId: 'pat-002',
    researchId: 'R01598',
    patientName: 'ItuHrs',
    diseaseName: '潰瘍性大腸炎',
    age: 38,
    gender: '男性',
    transplantCount: 6,
    recordDate: '2025-11-27',
    hospitalId: 'hosp-004',
    hospitalName: '二子玉川メディカルクリニック',
    doctorId: 'doc-004',
    doctorName: '佐藤 健一',
    recordType: 'doctor',
    subjectiveNote: '残念ながら変わりないです。',
    objectiveNote: '腹部膨満\n腸蠕動音亢進\n排便回数は1日7-8回、水様便',
    assessmentNote: '6回目の移植後も著明な改善は見られず。継続治療の検討が必要。',
    planNote: '追加検査を検討\n食事内容の見直し\n次回移植スケジュールを調整',
    additionalNote: '',
    createdAt: '2025-11-27T14:30:00Z',
    updatedAt: '2025-11-27T14:30:00Z',
  },
  {
    id: 'med-003',
    patientRecordId: 'pat-006',
    researchId: 'R00483',
    patientName: 'AihrChr',
    diseaseName: '過敏性腸症候群',
    age: 51,
    gender: '女性',
    transplantCount: 2,
    recordDate: '2025-11-26',
    hospitalId: 'hosp-003',
    hospitalName: '城谷バイオウェルネスクリニック',
    doctorId: 'doc-003',
    doctorName: '城谷 昌彦',
    recordType: 'doctor',
    subjectiveNote: '下痢改善傾向 お腹の張りが減った',
    objectiveNote: '腹部柔らかい\n排便回数1日3回',
    assessmentNote: '中程度の改善を認める。治療継続。',
    planNote: '次回移植を2週間後に予定\nプロバイオティクス継続',
    additionalNote: '',
    createdAt: '2025-11-26T09:00:00Z',
    updatedAt: '2025-11-26T09:00:00Z',
  },
  {
    id: 'med-004',
    patientRecordId: 'pat-007',
    researchId: 'R01700',
    patientName: 'KmrMki',
    diseaseName: 'アトピー性皮膚炎',
    age: 35,
    gender: '女性',
    transplantCount: 3,
    recordDate: '2025-11-25',
    hospitalId: 'hosp-002',
    hospitalName: 'サンテクリニック',
    doctorId: 'doc-002',
    doctorName: '山田 太郎',
    recordType: 'doctor',
    subjectiveNote: '皮膚症状の改善 かゆみが減った',
    objectiveNote: '発赤面積縮小\nかきむしり痕減少',
    assessmentNote: '著効。皮膚症状の改善を認める。',
    planNote: '保湿剤継続\n次回診察1ヶ月後',
    additionalNote: '',
    createdAt: '2025-11-25T11:00:00Z',
    updatedAt: '2025-11-25T11:00:00Z',
  },
  {
    id: 'med-005',
    patientRecordId: 'pat-008',
    researchId: 'R01701',
    patientName: 'YmdTky',
    diseaseName: 'うつ病',
    age: 48,
    gender: '男性',
    transplantCount: 4,
    recordDate: '2025-11-24',
    hospitalId: 'hosp-001',
    hospitalName: 'Baseクリニック赤坂',
    doctorId: 'doc-001',
    doctorName: '吉岡 紘平',
    recordType: 'doctor',
    subjectiveNote: '気分の落ち込みやイライラの改善 睡眠の改善',
    objectiveNote: '表情穏やか\n会話スムーズ',
    assessmentNote: '軽度改善。腸脳相関の観点から効果あり。',
    planNote: '精神科との連携継続\n次回移植を検討',
    additionalNote: '',
    createdAt: '2025-11-24T15:00:00Z',
    updatedAt: '2025-11-24T15:00:00Z',
  },
]

export const useMedicalRecordStore = create<MedicalRecordState>()(
  persist(
    (set, get) => ({
      records: initialRecords,

      addRecord: (recordData) => {
        const id = `med-${Date.now()}`
        const now = new Date().toISOString()
        const newRecord: MedicalRecord = {
          ...recordData,
          id,
          createdAt: now,
          updatedAt: now,
        }

        set((state) => ({
          records: [newRecord, ...state.records],
        }))

        return id
      },

      updateRecord: (id, data) => {
        set((state) => ({
          records: state.records.map((record) =>
            record.id === id
              ? { ...record, ...data, updatedAt: new Date().toISOString() }
              : record
          ),
        }))
      },

      deleteRecord: (id) => {
        set((state) => ({
          records: state.records.filter((record) => record.id !== id),
        }))
      },

      getRecord: (id) => {
        return get().records.find((record) => record.id === id)
      },

      getRecordsByPatient: (patientId) => {
        return get().records.filter((record) => record.patientRecordId === patientId)
      },

      getRecordsByHospital: (hospitalId) => {
        return get().records.filter((record) => record.hospitalId === hospitalId)
      },
    }),
    {
      name: 'fmt-japan-medical-records',
    }
  )
)



